import Joi from "joi-browser";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaHouse } from "react-icons/fa6";

import { getFieldOptions, getFieldValue } from "../../scripts/formScriptsv2";
import {
  deactivateForm,
  finalSignForm,
  getFormConfig,
  handleFormChange,
  loadSubmission,
  saveForm,
  saveRelatedObject,
  sendShareEmail,
  validateForm,
} from "../../scripts/formsyv2Scripts";
import BackIcon from "../common/BackIcon";
import FileUpload from "../common/FileUpload";
import FormButtonBar from "../common/FormButtonBar";
import FormHeader from "../common/FormHeader";
import {
  binti,
  getAttachments,
  getFosterHomeMembers,
  getSOARFosterHomeInfo,
  programUnitConfigs,
  sendEmailMessage,
} from "../services/soar";
import Comment from "./common/Comment";
import {
  FormInitials,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
  FormCheckbox,
} from "./common/ControlBundlev2";
import FormComments from "./common/FormComments";
import ShareModal from "./common/ShareModal";
import Signature from "./common/Signature";
import { getFieldName, getFieldText } from "../../scripts/helperScripts";

const versionId = "218D6942-80D2-EF11-88D0-F28FA7FAF263";
const zetoEnvironment =
  process.env.REACT_APP_ENVIRONMENT === "Production"
    ? "SOARQueryLive"
    : "SOARQueryBase";

function FHRulesInvestigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fosterhomeid, submissionid } = useParams();
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [data, setData] = useState({});
  const [touched, setTouched] = useState({});
  const [timeoutId, setTimeoutId] = useState();
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [controllerSignal, setControllerSignal] = useState();
  const [collateralOptions, setCollateralOptions] = useState([]);
  const pdfRef = useRef();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const modules = useMemo(() => {
    return safyInfo?.user?.modules || [];
  }, [safyInfo?.user?.modules]);

  const getFilteredSignatures = () => {
    const signatures = formsyDetails.Related?.filter(
      (r) => r.Active && r.ObjectType === "Signature"
    );

    return signatures || [];
  };

  const formDisabled = useMemo(() => {
    return !formsyDetails.Active || formsyDetails.FinalSigned;
  }, [formsyDetails.FinalSigned, formsyDetails.Active]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Any",
    };

    return formsyConfig.HelperConfiguration || defaultConfig;
  }, [formsyConfig.HelperConfiguration]);

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!controllerSignal || controllerSignal.aborted) {
      return;
    }

    const load = async () => {
      const promises = [
        getFormConfig(versionId),
        getSOARFosterHomeInfo(fosterhomeid, controllerSignal),
      ];

      if (submissionid) {
        promises.push(loadSubmission(submissionid));
      }

      const responses = await Promise.all(promises);

      if (controllerSignal.aborted) {
        return;
      }

      if (responses) {
        const si = JSON.parse(localStorage.getItem("safyInfo"));

        const config = responses[0];
        const fosterHome = responses[1];
        const address = fosterHome.addresses[0];

        setFormsyConfig({
          ...config,
          FormDescription: "Foster Home Rules Investigation",
          ContainerId: "EC878651-B6CF-EF11-88D0-F28FA7FAF263",
          SubmissionId: submissionid,
        });

        if (submissionid) {
          setData(responses[2].Results);
          setFormsyDetails(responses[2]);
        } else {
          //Set Formsy Details
          const newFormDetails = {
            SubmissionId: "",
            ContainerId: config.ContainerId,
            VersionId: config.VersionId,
            Results: {},
            Compliancy: null,
            Related: null,
            FinalSigned: false,
            FinalSignedDate: null,
            FinalSignedById: null,
            FinalSignedName: null,
            FormLocked: false,
            SignatureLocked: false,
            ModifiedById: si.user.azureId,
            ModifiedByName: si.user.displayName,
            Active: true,
          };
          setFormsyDetails(newFormDetails);

          const dataObj = { ...config.FormConfiguration };
          dataObj.FHSOARId.value = fosterHome.facility.soarid;
          dataObj.FHName.value = fosterHome.facility.name;
          dataObj.FHState.value = fosterHome.facility.state;
          dataObj.FHDivision.value = fosterHome.facility.division;
          dataObj.FHAddress.value = `${address.line1} ${address.city}, ${address.state} ${address.zip}`;
          dataObj.FHLicenseStart.value = DateTime.fromISO(
            fosterHome.license.start
          ).toLocaleString(DateTime.DATE_SHORT);
          dataObj.FHFacilityType.value = fosterHome.facility.types
            .map((t) => t.text)
            .join(", ");
          dataObj.FHLicenseType.value = fosterHome.license.types
            .map((t) => t.text)
            .join(", ");
          setData(dataObj);
        }
      }
    };

    load();
  }, [controllerSignal, submissionid, fosterhomeid]);

  const getJoiSchema = (dataObj) => {
    const dateFields = [
      {
        name: "SAFYAwareDate",
        value: DateTime.fromISO(getFieldValue(dataObj.SAFYAwareDate)),
      },
      {
        name: "DateOfExternal",
        value: DateTime.fromISO(getFieldValue(dataObj.DateOfExternal)),
      },
      {
        name: "DateOfAppeal",
        value: DateTime.fromISO(getFieldValue(dataObj.DateOfAppeal)),
      },
      {
        name: "DateOfInternal",
        value: DateTime.fromISO(getFieldValue(dataObj.DateOfInternal)),
      },
      {
        name: "PIPDateCompleted",
        value: DateTime.fromISO(getFieldValue(dataObj.PIPDateCompleted)),
      },
    ].sort((a, b) => {
      return a.value < b.value ? 1 : a.value > b.value ? -1 : 0;
    });

    return {
      FHSOARId: Joi.any(),
      FHName: Joi.any(),
      FHState: Joi.any(),
      FHDivision: Joi.any(),
      FHAddress: Joi.string().required().label("Foster Home Address"),
      FHLicenseStart: Joi.any(),
      FHFacilityType: Joi.any(),
      FHLicenseType: Joi.any(),
      SAFYAwareDate: Joi.date()
        .max("now")
        .when("DateOfViolation", {
          is: Joi.date().required(),
          then: Joi.date().min(Joi.ref("DateOfViolation")),
        })
        .required()
        .label("Date SAFY made aware")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `"Date SAFY made aware" cannot be future dated`;
                break;
              case "date.min":
                error.message = `"Date SAFY made aware" needs to be on or after "Date of violation"`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      DateOfViolation: Joi.date()
        .max("now")
        .required()
        .label("Date of violation")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `"Date of violation" cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      RuleInfo: Joi.string()
        .required()
        .label("Details of regulation/policy violated"),
      ClientInitials: Joi.string().required().label("Client Initials"),
      UploadingCAP: Joi.when("FHState", {
        is: "Indiana",
        then: Joi.string().required().label("Are you uploading a State CAP?"),
      }),
      Category: Joi.string().required().label("Alleged violation category"),
      Subcategory: Joi.string().required().label("Subcategory"),
      SubcategoryOther: Joi.when("Subcategory", {
        is: "Other",
        then: Joi.string().required().label("Other subcategory"),
      }),
      Description: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.string().required().label("Violation description"),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.string().required().label("Violation description"),
          }),
        }),
      }),
      PIPRequired: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.string().required().label("PIP requirement"),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.string().required().label("PIP requirement"),
          }),
        }),
      }),
      MustReport: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.string().required().label("Must report?"),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.string().required().label("Must report?"),
          }),
        }),
      }),
      DateOfReport: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("MustReport", {
            is: "Yes",
            then: Joi.date()
              .max("now")
              .greater(Joi.ref("DateOfViolation"))
              .required(),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("MustReport", {
              is: "Yes",
              then: Joi.date()
                .max("now")
                .greater(Joi.ref("DateOfViolation"))
                .required(),
            }),
          }),
        }),
      })
        .label("Reported date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.greater":
                error.message = `"Reported date" needs to be after "Date of violation"`;
                break;
              case "date.max":
                error.message = `"Reported date" cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      ReportedTo: Joi.any(),
      NoReportReason: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("MustReport", {
            is: "No",
            then: Joi.string().required().label("Reason for not reporting"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("MustReport", {
              is: "No",
              then: Joi.string().required().label("Reason for not reporting"),
            }),
          }),
        }),
      }),
      Investigation: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.string()
            .required()
            .label("Third-party investigation?"),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.string()
              .required()
              .label("Third-party investigation?"),
          }),
        }),
      }),
      HoldInternal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.string().required().label("Hold internal investigation?"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.string()
                .required()
                .label("Hold internal investigation?"),
            }),
          }),
        }),
      }),
      ReceivedResults: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.string().required().label("Received results?"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.string().required().label("Received results?"),
            }),
          }),
        }),
      }),
      Impact: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.string().required(),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.string().required(),
          }),
        }),
      }),
      Removal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Impact", {
            is: Joi.string()
              .required()
              .valid(
                "Children placed in respite",
                "Children transferred to new home"
              )
              .label("Removal from home"),
            then: Joi.string().required().label("Removal from home"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Impact", {
              is: Joi.string()
                .required()
                .valid(
                  "Children placed in respite",
                  "Children transferred to new home"
                )
                .label("Removal from home"),
              then: Joi.string().required().label("Removal from home"),
            }),
          }),
        }),
      }),
      DateOfExternal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.date()
                .max("now")
                .greater(Joi.ref("DateOfViolation"))
                .required()
                .error((errors) => {
                  errors.forEach((error) => {
                    switch (error.type) {
                      case "date.greater":
                        error.message = `"Date of external investigation outcome" needs to be after "Date of violation"`;
                        break;
                      case "date.max":
                        error.message = `"Date of external investigation outcome" cannot be future dated`;
                        break;
                      default:
                        return error.message;
                    }
                  });
                  return errors;
                }),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.date()
                  .max("now")
                  .greater(Joi.ref("DateOfViolation"))
                  .required()
                  .error((errors) => {
                    errors.forEach((error) => {
                      switch (error.type) {
                        case "date.greater":
                          error.message = `"Date of external investigation outcome" needs to be after "Date of violation"`;
                          break;
                        case "date.max":
                          error.message = `"Date of external investigation outcome" cannot be future dated`;
                          break;
                        default:
                          return error.message;
                      }
                    });
                    return errors;
                  }),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.date()
                  .max("now")
                  .greater(Joi.ref("DateOfViolation"))
                  .required()
                  .error((errors) => {
                    errors.forEach((error) => {
                      switch (error.type) {
                        case "date.greater":
                          error.message = `"Date of external investigation outcome" needs to be after "Date of violation"`;
                          break;
                        case "date.max":
                          error.message = `"Date of external investigation outcome" cannot be future dated`;
                          break;
                        default:
                          return error.message;
                      }
                    });
                    return errors;
                  }),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.date()
                    .max("now")
                    .greater(Joi.ref("DateOfViolation"))
                    .required()
                    .error((errors) => {
                      errors.forEach((error) => {
                        switch (error.type) {
                          case "date.greater":
                            error.message = `"Date of external investigation outcome" needs to be after "Date of violation"`;
                            break;
                          case "date.max":
                            error.message = `"Date of external investigation outcome" cannot be future dated`;
                            break;
                          default:
                            return error.message;
                        }
                      });
                      return errors;
                    }),
                }),
              }),
            }),
          }),
        }),
      }).label("Date of external investigation outcome"),
      ExternalOutcome: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string()
                .required()
                .label("External investigation outcome"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string()
                  .required()
                  .label("External investigation outcome"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string()
                  .required()
                  .label("External investigation outcome"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string()
                    .required()
                    .label("External investigation outcome"),
                }),
              }),
            }),
          }),
        }),
      }),
      ExternalDetails: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string()
                .required()
                .label("External investigation description"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string()
                  .required()
                  .label("External investigation description"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string()
                  .required()
                  .label("External investigation description"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string()
                    .required()
                    .label("External investigation description"),
                }),
              }),
            }),
          }),
        }),
      }),
      Appeal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string().required().label("Foster Parent will appeal?"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string()
                  .required()
                  .label("Foster Parent will appeal?"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string()
                  .required()
                  .label("Foster Parent will appeal?"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string()
                    .required()
                    .label("Foster Parent will appeal?"),
                }),
              }),
            }),
          }),
        }),
      }),
      DateOfAppeal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.when("Appeal", {
                is: "Yes",
                then: Joi.date().min(Joi.ref("DateOfExternal")).required(),
              }),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.when("Appeal", {
                  is: "Yes",
                  then: Joi.date().min(Joi.ref("DateOfExternal")).required(),
                }),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.when("Appeal", {
                  is: "Yes",
                  then: Joi.date().min(Joi.ref("DateOfExternal")).required(),
                }),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.when("Appeal", {
                    is: "Yes",
                    then: Joi.date().min(Joi.ref("DateOfExternal")).required(),
                  }),
                }),
              }),
            }),
          }),
        }),
      })
        .label("Date of appeal")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `"Date of appeal" needs to be on or after "Date of external investigation outcome"`;
                break;
              case "date.max":
                error.message = `"Date of appeal" cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      ResultOfAppeal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.when("Appeal", {
                is: "Yes",
                then: Joi.string().required().label("Result of appeal"),
              }),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.when("Appeal", {
                  is: "Yes",
                  then: Joi.string().required().label("Result of appeal"),
                }),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.when("Appeal", {
                  is: "Yes",
                  then: Joi.string().required().label("Result of appeal"),
                }),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.when("Appeal", {
                    is: "Yes",
                    then: Joi.string().required().label("Result of appeal"),
                  }),
                }),
              }),
            }),
          }),
        }),
      }),
      DateOfInternal: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "No",
            then: Joi.date()
              .max("now")
              .min(Joi.ref("SAFYAwareDate"))
              .required()
              .error((errors) => {
                errors.forEach((error) => {
                  switch (error.type) {
                    case "date.min":
                      error.message = `"Date of internal investigation" needs to be on or after "Date SAFY made aware"`;
                      break;
                    case "date.max":
                      error.message = `"Date of internal investigation" cannot be future dated`;
                      break;
                    default:
                      return error.message;
                  }
                });
                return errors;
              }),
          }).when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.date()
                .max("now")
                .min(Joi.ref("SAFYAwareDate"))
                .required()
                .error((errors) => {
                  errors.forEach((error) => {
                    switch (error.type) {
                      case "date.min":
                        error.message = `"Date of internal investigation" needs to be on or after "Date SAFY made aware"`;
                        break;
                      case "date.max":
                        error.message = `"Date of internal investigation" cannot be future dated`;
                        break;
                      default:
                        return error.message;
                    }
                  });
                  return errors;
                }),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.date()
                  .max("now")
                  .min(Joi.ref("DateOfExternal"))
                  .required()
                  .error((errors) => {
                    errors.forEach((error) => {
                      switch (error.type) {
                        case "date.min":
                          error.message = `"Date of internal investigation" needs to be on or after "Date of external investigation outcome"`;
                          break;
                        case "date.max":
                          error.message = `"Date of internal investigation" cannot be future dated`;
                          break;
                        default:
                          return error.message;
                      }
                    });
                    return errors;
                  }),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "No",
              then: Joi.date()
                .max("now")
                .min(Joi.ref("SAFYAwareDate"))
                .required()
                .error((errors) => {
                  errors.forEach((error) => {
                    switch (error.type) {
                      case "date.min":
                        error.message = `"Date of internal investigation" needs to be on or after "Date SAFY made aware"`;
                        break;
                      case "date.max":
                        error.message = `"Date of internal investigation" cannot be future dated`;
                        break;
                      default:
                        return error.message;
                    }
                  });
                  return errors;
                }),
            }).when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.date()
                  .max("now")
                  .min(Joi.ref("SAFYAwareDate"))
                  .required()
                  .error((errors) => {
                    errors.forEach((error) => {
                      switch (error.type) {
                        case "date.min":
                          error.message = `"Date of internal investigation" needs to be on or after "Date SAFY made aware"`;
                          break;
                        case "date.max":
                          error.message = `"Date of internal investigation" cannot be future dated`;
                          break;
                        default:
                          return error.message;
                      }
                    });
                    return errors;
                  }),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.date()
                    .max("now")
                    .min(Joi.ref("DateOfExternal"))
                    .required()
                    .error((errors) => {
                      errors.forEach((error) => {
                        switch (error.type) {
                          case "date.min":
                            error.message = `"Date of internal investigation" needs to be on or after "Date of external investigation outcome"`;
                            break;
                          case "date.max":
                            error.message = `"Date of internal investigation" cannot be future dated`;
                            break;
                          default:
                            return error.message;
                        }
                      });
                      return errors;
                    }),
                }),
              }),
            }),
          }),
        }),
      }).label("Date of internal investigation"),
      InternalOutcome: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "No",
            then: Joi.string()
              .required()
              .label("Internal investigation outcome"),
          }).when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string()
                .required()
                .label("Internal investigation outcome"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string()
                  .required()
                  .label("Internal investigation outcome"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "No",
              then: Joi.string()
                .required()
                .label("Internal investigation outcome"),
            }).when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string()
                  .required()
                  .label("Internal investigation outcome"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string()
                    .required()
                    .label("Internal investigation outcome"),
                }),
              }),
            }),
          }),
        }),
      }),
      InternalDetails: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "No",
            then: Joi.string()
              .required()
              .label("Internal investigation description"),
          }).when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string()
                .required()
                .label("Internal investigation description"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string()
                  .required()
                  .label("Internal investigation description"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "No",
              then: Joi.string()
                .required()
                .label("Internal investigation description"),
            }).when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string()
                  .required()
                  .label("Internal investigation description"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string()
                    .required()
                    .label("Internal investigation description"),
                }),
              }),
            }),
          }),
        }),
      }),
      Status: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.any(),
          otherwise: Joi.when("Investigation", {
            is: "No",
            then: Joi.string().required().label("Foster Home status"),
          }).when("Investigation", {
            is: "Yes",
            then: Joi.when("HoldInternal", {
              is: "No",
              then: Joi.string().required().label("Foster Home status"),
            }).when("HoldInternal", {
              is: "Yes",
              then: Joi.when("ReceivedResults", {
                is: "Yes",
                then: Joi.string().required().label("Foster Home status"),
              }),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.any(),
            otherwise: Joi.when("Investigation", {
              is: "No",
              then: Joi.string().required().label("Foster Home status"),
            }).when("Investigation", {
              is: "Yes",
              then: Joi.when("HoldInternal", {
                is: "No",
                then: Joi.string().required().label("Foster Home status"),
              }).when("HoldInternal", {
                is: "Yes",
                then: Joi.when("ReceivedResults", {
                  is: "Yes",
                  then: Joi.string().required().label("Foster Home status"),
                }),
              }),
            }),
          }),
        }),
      }),
      PIPDescription: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.string().required().label("PIP description"),
          otherwise: Joi.when("PIPRequired", {
            is: "Yes",
            then: Joi.string().required().label("PIP description"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.string().required().label("PIP description"),
            otherwise: Joi.when("PIPRequired", {
              is: "Yes",
              then: Joi.string().required().label("PIP description"),
            }),
          }),
        }),
      }),
      PIPDateImplemented: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.date().max("now").min(Joi.ref("SAFYAwareDate")).required(),
          otherwise: Joi.when("PIPRequired", {
            is: "Yes",
            then: Joi.date()
              .max("now")
              .min(Joi.ref("SAFYAwareDate"))
              .required(),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.date()
              .max("now")
              .min(Joi.ref("SAFYAwareDate"))
              .required(),
            otherwise: Joi.when("PIPRequired", {
              is: "Yes",
              then: Joi.date()
                .max("now")
                .min(Joi.ref("SAFYAwareDate"))
                .required(),
            }),
          }),
        }),
      })
        .label("PIP implementation date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `"PIP implementation date" needs to be on or after "Date SAFY made aware"`;
                break;
              case "date.max":
                error.message = `"PIP implementation date" cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      PIPDateAnticipated: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.date().greater(Joi.ref("PIPDateImplemented")).required(),
          otherwise: Joi.when("PIPRequired", {
            is: "Yes",
            then: Joi.date().greater(Joi.ref("PIPDateImplemented")).required(),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.date().greater(Joi.ref("PIPDateImplemented")).required(),
            otherwise: Joi.when("PIPRequired", {
              is: "Yes",
              then: Joi.date()
                .greater(Joi.ref("PIPDateImplemented"))
                .required(),
            }),
          }),
        }),
      })
        .label("Anticipated completion date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.greater":
                error.message = `"Anticipated completion date" needs to be after "PIP implementation date"`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      PIPOnHold: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.string().required().label("Home placed on hold"),
          otherwise: Joi.when("PIPRequired", {
            is: "Yes",
            then: Joi.string().required().label("Home placed on hold"),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.string().required().label("Home placed on hold"),
            otherwise: Joi.when("PIPRequired", {
              is: "Yes",
              then: Joi.string().required().label("Home placed on hold"),
            }),
          }),
        }),
      }),
      PIPOnHoldDate: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("PIPOnHold", {
          is: "Yes",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.date()
              .max("now")
              .min(Joi.ref("SAFYAwareDate"))
              .required(),
            otherwise: Joi.when("PIPRequired", {
              is: "Yes",
              then: Joi.date()
                .max("now")
                .min(Joi.ref("SAFYAwareDate"))
                .required(),
            }),
          }),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("PIPOnHold", {
            is: "Yes",
            then: Joi.when("Category", {
              is: "Foster Parent PIP Only",
              then: Joi.date()
                .max("now")
                .min(Joi.ref("SAFYAwareDate"))
                .required(),
              otherwise: Joi.when("PIPRequired", {
                is: "Yes",
                then: Joi.date()
                  .max("now")
                  .min(Joi.ref("SAFYAwareDate"))
                  .required(),
              }),
            }),
          }),
        }),
      })
        .label("Date placed on hold")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `"Date placed on hold" cannot be future dated`;
                break;
              case "date.min":
                error.message = `"Date placed on hold" needs to be on or after "Date SAFY made aware"`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      PIPMonitoredBy: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.string().required().label("Monitored by"),
        }).when("PIPRequired", {
          is: "Yes",
          then: Joi.string().required().label("Monitored by"),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.string().required().label("Monitored by"),
          }),
        }).when("PIPRequired", {
          is: "Yes",
          then: Joi.string().required().label("Monitored by"),
        }),
      }),
      PIPDateCompleted: Joi.when("FHState", {
        is: Joi.required().valid(
          "Alabama",
          "Colorado",
          "Nevada",
          "South Carolina",
          "Kentucky"
        ),
        then: Joi.when("Category", {
          is: "Foster Parent PIP Only",
          then: Joi.date()
            .max("now")
            .greater(Joi.ref("PIPDateImplemented"))
            .required(),
        }).when("PIPRequired", {
          is: "Yes",
          then: Joi.date()
            .max("now")
            .greater(Joi.ref("PIPDateImplemented"))
            .required(),
        }),
        otherwise: Joi.when("UploadingCAP", {
          is: "No",
          then: Joi.when("Category", {
            is: "Foster Parent PIP Only",
            then: Joi.date()
              .max("now")
              .greater(Joi.ref("PIPDateImplemented"))
              .required(),
            otherwise: Joi,
          }).when("PIPRequired", {
            is: "Yes",
            then: Joi.date()
              .max("now")
              .greater(Joi.ref("PIPDateImplemented"))
              .required(),
          }),
        }),
      })
        .label("Date completed by FP")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.greater":
                error.message = `"Date completed by FP" needs to be after "PIP implementation date"`;
                break;
              case "date.max":
                error.message = `"Date completed by FP" cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      InvestigationClosedDate:
        getFilteredSignatures().length > 0
          ? Joi.date()
              .max("now")
              .when(dateFields[0].name, {
                is: Joi.date().required(),
                then: Joi.date().greater(Joi.ref(dateFields[0].name)),
              })
              .required()
              .label("Investigation Closed Date")
              .error((errors) => {
                errors.forEach((error) => {
                  switch (error.type) {
                    case "date.greater":
                      error.message = `"Date Foster Home Rules Investigation was closed" needs to be after all other dates`;
                      break;
                    case "date.max":
                      error.message = `"Date Foster Home Rules Investigation was closed" cannot be future dated`;
                      break;
                    default:
                      return error.message;
                  }
                });
                return errors;
              })
          : Joi.any(),
      InvestigationComplete: Joi.boolean(),
      DocumentsUploaded: Joi.boolean(),
      RulesInvestigationClosed: Joi.boolean(),
    };
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, getJoiSchema(data));
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, getJoiSchema(data), errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (dataObj, autoSave = false) => {
    if (isSaveInProgress) {
      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    setIsSaveInProgress(true);

    try {
      const formData = {
        ...formsyConfig,
        PrimaryObjectId: fosterhomeid,
        PrimaryObjectName: dataObj.FHName.value,
        PrimaryObjectType: "Foster Home",
        Results: { ...dataObj },
      };
      const submissionIdResponse = await saveForm(
        formData,
        !submissionid ? "Insert" : "Update"
      );

      if (submissionIdResponse?.code === "success") {
        if (!submissionid) {
          navigate(
            `/fosterhomes/${fosterhomeid}/rules/${submissionIdResponse.id}`,
            {
              replace: true,
              state: location.state,
            }
          );
        } else {
          setFormsyDetails((fd) => {
            const details = { ...fd };
            details.ModifiedOn = DateTime.utc().toISO();
            details.ModifiedById = safyInfo.user.azureId;
            details.ModifiedByName = safyInfo.user.displayName;
            return details;
          });
        }
      } else {
        console.log("Form Save Error", submissionIdResponse);
      }
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////DELETE
  const handleDelete = async (e) => {
    e.preventDefault();
    if (submissionid) {
      deactivateForm(submissionid);
      navigate(`/fosterhomes/${fosterhomeid}`, {
        replace: true,
        state: { ...location.state, tab: "rules" },
      });
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const getShareLogs = useCallback(() => {
    const comments = formsyDetails.Related?.filter(
      (r) => r.Active && r.ObjectType === "Share"
    );

    return comments || [];
  }, [formsyDetails.Related]);

  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

    saveRelatedObject(submissionid, {
      action: "insert",
      objectJson: {
        comment: shareComments,
        sharedBy: safyInfo.user.displayName,
        sharedTo: recipientList,
      },
      objectType: "Share",
    });
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const getAllComments = useCallback(() => {
    const comments = formsyDetails.Related?.filter(
      (r) => r.Active && r.ObjectType === "Comment"
    );

    return comments || [];
  }, [formsyDetails.Related]);

  const getFieldComments = useCallback(
    (field) => {
      return getAllComments().filter((c) => c.ObjectJSON.field === field);
    },
    [getAllComments]
  );

  const toggleCommentModal = (comment) => {
    setCommentToEdit(comment);
    setShowComment(!showComment);
  };

  const commentFields = useMemo(() => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form" });
    for (const property in formsyConfig.FormConfiguration) {
      if (
        formsyConfig.FormConfiguration[property] &&
        formsyConfig.FormConfiguration[property].type !== "section" &&
        formsyConfig.FormConfiguration[property].type !== "label" &&
        formsyConfig.FormConfiguration[property].type !== "User" &&
        formsyConfig.FormConfiguration[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name: formsyConfig.FormConfiguration[property].label || property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  }, [formsyConfig.FormConfiguration]);

  const onCommentSave = async (comment) => {
    toggleCommentModal();
    setCommentToEdit(undefined);

    const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
    const relatedObj = {
      action: "insert",
      objectJson: {
        ...comment.ObjectJSON,
        createdBy: safyInfo.user.displayName,
        createdOn: DateTime.now().toLocaleString({
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
        }),
      },
      objectType: "Comment",
    };

    if (comment.RelatedId) {
      relatedObj.action = "update";
      relatedObj.relatedId = comment.RelatedId;
      const response = await saveRelatedObject(submissionid, relatedObj);

      if (response.code === "success") {
        toast.success("Comment saved");
        setFormsyDetails((fd) => {
          const obj = { ...fd };
          const originalIndex = obj.Related.findIndex(
            (r) => r.RelatedId === comment.RelatedId
          );
          obj.Related[originalIndex] = comment;
          return obj;
        });
      } else {
        toast.error("Error saving comment");
        console.log(response);
      }
    } else {
      const response = await saveRelatedObject(submissionid, relatedObj);
      if (response.code === "success") {
        toast.success("Comment saved");
        const newComment = {
          RelatedId: response.id,
          SubmissionId: submissionid,
          ObjectJSON: {
            ...relatedObj.objectJson,
          },
          ObjectType: "Comment",
          Active: true,
        };
        setFormsyDetails((fd) => {
          const obj = { ...fd };
          obj.Related.push(newComment);
          return obj;
        });
      } else {
        toast.error("Error saving comment");
      }
    }
  };

  const handleDeleteComment = async (comment) => {
    const response = await saveRelatedObject(submissionid, {
      action: "deactivate",
      relatedId: comment.RelatedId,
    });

    if (response.code === "success") {
      toast.success("Comment deleted");
      setFormsyDetails((fd) => {
        const obj = { ...fd };
        const originalIndex = obj.Related.findIndex(
          (r) => r.RelatedId === comment.RelatedId
        );
        obj.Related.splice(originalIndex, 1);
        return obj;
      });
    } else {
      toast.error("Error deleting comment");
    }
  };

  const handleAcknowledgeComment = async (comment, acknowledge) => {
    const commentObj = {
      action: "update",
      relatedId: comment.RelatedId,
      objectJson: comment.ObjectJSON,
      objectType: "Comment",
    };

    if (acknowledge) {
      commentObj.objectJson.acknowledgedBy = safyInfo.user.displayName;
      commentObj.objectJson.acknowledgedOn = DateTime.now().toLocaleString({
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "numeric",
        minutes: "numeric",
      });
    } else {
      delete commentObj.objectJson.acknowledgedBy;
      delete commentObj.objectJson.acknowledgedOn;
    }

    const response = await saveRelatedObject(submissionid, commentObj);

    if (response.code === "success") {
      toast.success("Comment acknowledged");
      setFormsyDetails((fd) => {
        const obj = { ...fd };
        const originalIndex = obj.Related.findIndex(
          (r) => r.RelatedId === comment.RelatedId
        );
        obj.Related[originalIndex].ObjectJSON = commentObj.objectJson;
        return obj;
      });
    } else {
      toast.error("Error acknowledging comment");
      console.log(response);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const commentsFactory = {
    add: toggleCommentModal,
    edit: toggleCommentModal,
    delete: handleDeleteComment,
    acknowledge: handleAcknowledgeComment,
    get: getFieldComments,
    navigate: handleCommentNavigation,
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress && emailConfig !== undefined)
      setShowSignatureModal(true);
  };

  const getMainEmailBody = () => {
    const siteUrl =
      process.env.REACT_APP_ENVIRONMENT === "Production"
        ? "safyportal.safy.org"
        : "safyportaltest.safy.org";

    return `
  <p>
    <b>Foster Home:</b> ${getFieldValue(data.FHName)}
    <br/>
    <b>Division:</b> ${getFieldValue(data.FHDivision)}
  </p>
  <p>
    https://${siteUrl}/share${location.pathname}
  </p>
  <p>  
    <b>Date of Violation:</b> ${DateTime.fromISO(
      getFieldValue(data.DateOfViolation)
    ).toLocaleString(DateTime.DATE_SHORT)}
    <br/>
    <b>Alleged Rule/Policy Violation Category:</b> ${getFieldValue(
      data.Category
    )}
  </p>
  ${
    getFieldValue(data.Category) !== "Foster Parent PIP Only" &&
    getFieldValue(data.Investigation) === "Yes" &&
    getFieldValue(data.ReceivedResults) === "Yes"
      ? `<p>
    <b>External Outcome:</b> ${getFieldValue(data.ExternalOutcome)}
  </p>`
      : ""
  }
  ${
    getFieldValue(data.Category) !== "Foster Parent PIP Only" &&
    (getFieldValue(data.Investigation) === "No" ||
      getFieldValue(data.HoldInternal) === "No" ||
      (getFieldValue(data.HoldInternal) === "Yes" &&
        getFieldValue(data.ReceivedResults) === "Yes"))
      ? `<p>
    <b>Internal Outcome:</b> ${getFieldValue(data.InternalOutcome)}
  </p>`
      : ""
  }
  `;
  };

  const saveSignature = async (signatureData) => {
    setShowSignatureModal(false);

    if (
      signatureData.signatureType.toLowerCase() ===
        helperConfiguration.finalSignType.toLowerCase() &&
      !supervisorCompleted
    ) {
      toast.warn('"Supervisor Use Only" section must be completed');
      return;
    }

    const relatedObj = {
      action: "insert",
      objectJson: signatureData,
      objectType: "Signature",
    };
    const response = await saveRelatedObject(
      formsyConfig.SubmissionId,
      relatedObj
    );
    if (response.code === "success") {
      const newSignature = {
        RelatedId: response.id,
        SubmissionId: submissionid,
        ObjectJSON: {
          ...relatedObj.objectJson,
        },
        ObjectType: "Signature",
        Active: true,
      };

      if (
        supervisorCompleted &&
        signatureData.signatureType.toLowerCase() ===
          helperConfiguration.finalSignType.toLowerCase()
      ) {
        const finalSignResponse = await finalSignForm(submissionid);
        if (finalSignResponse.code === "success") {
          setFormsyDetails((fd) => {
            const obj = { ...fd };

            if (!obj.Related) {
              obj.Related = [newSignature];
            } else {
              obj.Related.push(newSignature);
            }

            if (!obj.FinalSigned) {
              obj.FinalSigned = true;
              obj.FinalSignedDate = DateTime.now().toISO();
              obj.FinalSignedById = safyInfo.user.safyInfo.SOARUserId;
              obj.FinalSignedName = safyInfo.user.displayName;
            }

            return obj;
          });

          if (emailConfig.FHRulesSigned) {
            sendEmailMessage(
              emailConfig.FHRulesSigned,
              undefined,
              `Foster Home Rules Investigation Final Signed`,
              `${getMainEmailBody()}
            <p><b>Signed by</b>: ${safyInfo.user.displayName}</p>`
            );
          }
        }
      } else {
        setFormsyDetails((fd) => {
          const obj = { ...fd };

          if (!obj.Related) {
            obj.Related = [newSignature];
          } else {
            obj.Related.push(newSignature);
          }

          return obj;
        });
      }
    }
  };

  //CUSTOM
  const [emailConfig, setEmailConfig] = useState();
  const [fileAttachments, setFileAttachments] = useState([]);
  const [bintiApplicationId, setBintiApplicationId] = useState();

  useEffect(() => {
    const loadFosterHomeMembers = async () => {
      const response = await getFosterHomeMembers(fosterhomeid);

      if (response?.length > 0) {
        setCollateralOptions(
          response.map((r) => ({
            _id: getFieldText(r.ccx_contact),
            name: `${r.ccx_name} - ${getFieldName(r.ccx_homemembertype)}`,
            contactName: r.ccx_name,
            contactRole: getFieldName(r.ccx_homemembertype),
          }))
        );
      }
    };

    loadFosterHomeMembers();
  }, []);

  useEffect(() => {
    if (data.FHSOARId?.value) {
      const loadBinti = async () => {
        const body = {
          command: "query",
          keyword: "byId",
          ids: [data.FHSOARId.value],
        };
        const response = await binti(body);

        if (response?.code === "success") {
          setBintiApplicationId(response.data[0].homedata[0]["application-id"]);
        }
      };

      loadBinti();
    }
  }, [data.FHSOARId?.value]);

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    if (data.FHDivision?.value && emailConfig === undefined) {
      const getProgramUnitCongfig = async () => {
        const response = await programUnitConfigs({
          command: "query",
          environment: zetoEnvironment,
          keyword: "programnameinterm",
          searchTerms: {
            programName: `${data.FHDivision.value} - Foster Homes`,
          },
        });

        if (response.code === "success") {
          const result = response.data[0];

          if (!result.sor_irconfig) {
            setEmailConfig(null);
          } else {
            try {
              setEmailConfig(JSON.parse(result.sor_irconfig));
            } catch (error) {
              toast.error(`Error parsing Program Unit email config.`);
            }
          }
        } else {
          toast.error(`Error in loading Program Units. ${response.message}`);
        }
      };

      getProgramUnitCongfig();
    }
  }, [data.FHDivision?.value, emailConfig]);

  const getCategoryDefinition = (categoryValue, categoryOptions) => {
    if (!categoryValue || !categoryOptions) {
      return "";
    }

    const selected = categoryOptions.filter((d) => d._id === categoryValue)[0];
    return selected?.definition || "";
  };

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const customValidate = (errs) => {
    if (
      (getFieldValue(data.FHState) === "Ohio" ||
        (getFieldValue(data.FHState) === "Indiana" &&
          getFieldValue(data.UploadingCAP) === "Yes")) &&
      fileAttachments.length === 0
    ) {
      if (errs) {
        errs.FileAttachments = "At least one document must be uploaded.";
      } else {
        errs = {
          FileAttachments: "At least one document must be uploaded.",
        };
      }
    }

    setErrors(errs);
    return errs;
  };

  useEffect(() => {
    if (data.Subcategory?.value !== "Other") {
      setData((prevData) => {
        const obj = { ...prevData };

        if (obj.SubcategoryOther) {
          obj.SubcategoryOther.value = "";
        } else {
          obj.SubcategoryOther = {
            value: "",
            type: "custom",
          };
        }

        return obj;
      });
    }
  }, [data.Subcategory?.value]);

  const supervisorCompleted = useMemo(() => {
    return (
      data.InvestigationClosedDate?.value &&
      data.InvestigationComplete?.value &&
      data.DocumentsUploaded?.value &&
      data.RulesInvestigationClosed?.value
    );
  }, [
    data.InvestigationClosedDate?.value,
    data.InvestigationComplete?.value,
    data.DocumentsUploaded?.value,
    data.RulesInvestigationClosed?.value,
  ]);

  const handleChangeClearPIPRequired = (e, clear) => {
    const d = handleChange(e);

    if (clear) {
      d.PIPRequired.value = "";
      setData(d);
    }
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <FormHeader
          formTitle={formsyConfig.FormDescription}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={() => {
            handleSubmit(data);
          }}
          onDelete={submissionid ? handleDelete : null}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onShare={toggleShareModal}
          signed={getFilteredSignatures().length > 0}
          finalSigned={formsyConfig.FinalSigned === "1"}
          validate={validate}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          pdfRef={pdfRef}
          hidePrint={true}
          customValidate={customValidate}
        />
        <FormComments
          comments={getAllComments()}
          commentsFactory={commentsFactory}
        />
        <div className="mt-2">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="h5">Home Details</div>
              <div className="container-sm">
                <div className="row gy-2 mb-3">
                  <div className="col">
                    Foster Home Name
                    <br />
                    <div className="d-flex fw-bold align-items-center">
                      {getFieldValue(data.FHName)}
                      {bintiApplicationId ? (
                        <FaHouse
                          className="ms-4 text-safy-info"
                          size="1.25rem"
                          title="Open Foster Home in Binti"
                          onClick={() =>
                            window.open(
                              `https://family.binti.com/admin/applications/${bintiApplicationId}`
                            )
                          }
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="col">
                    Division
                    <br />
                    <b>{getFieldValue(data.FHDivision)}</b>
                  </div>
                </div>
                <div className="row gy-2">
                  <div className="col">
                    Foster Home Address
                    <br />
                    <FormTextArea
                      name="FHAddress"
                      error={errors.FHAddress}
                      value={getFieldValue(data.FHAddress)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row gy-2 mb-3">
                  <div className="col">
                    License Type(s)
                    <br />
                    <b>{getFieldValue(data.FHLicenseType)}</b>
                  </div>
                  <div className="col">
                    Facility Type(s)
                    <br />
                    <b>{getFieldValue(data.FHFacilityType)}</b>
                  </div>
                </div>
                <div className="row gy-2 mb-3">
                  <div className="col">
                    License Start Date
                    <br />
                    <b>{getFieldValue(data.FHLicenseStart)}</b>
                  </div>
                </div>
              </div>
              <div className="h5">Background Information</div>
              <div className="container-sm">
                <div className="row row-cols-1 row-cols-md-2 gy-2">
                  <div className="col">
                    <FormInput
                      name="SAFYAwareDate"
                      label="When was SAFY made aware of the alleged rule/policy violation?"
                      error={errors.SAFYAwareDate}
                      value={getFieldValue(data.SAFYAwareDate)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col">
                    <FormInput
                      name="DateOfViolation"
                      label="When did the alleged rule/policy violation occur?"
                      error={errors.DateOfViolation}
                      value={getFieldValue(data.DateOfViolation)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
                <FormInput
                  name="RuleInfo"
                  label="Name and number of the state regulation and/or SAFY policy violated"
                  error={errors.RuleInfo}
                  value={getFieldValue(data.RuleInfo)}
                  onChange={handleChange}
                  commentsFactory={commentsFactory}
                  disabled={formDisabled}
                />
                <FormInitials
                  name="ClientInitials"
                  label="Initials of client involved in rules violation"
                  error={errors.ClientInitials}
                  value={getFieldValue(data.ClientInitials)}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {getFieldValue(data.FHState) === "Indiana" ? (
                  <FormRadioButton
                    name="UploadingCAP"
                    label="Are you uploading a State CAP?"
                    error={errors.UploadingCAP}
                    value={getFieldValue(data.UploadingCAP)}
                    options={yesNoOptions}
                    onChange={(e) => {
                      handleChangeClearPIPRequired(
                        e,
                        e.currentTarget.value === "No"
                      );
                    }}
                    commentsFactory={commentsFactory}
                    disabled={formDisabled}
                    divClassName="mb-3"
                  />
                ) : null}
                <div className="row row-cols-1 row-cols-lg-2">
                  <div className="col">
                    <FormSelect
                      name="Category"
                      label="Alleged rule/policy violation category"
                      error={errors.Category}
                      value={getFieldValue(data.Category)}
                      options={getFieldOptions(
                        formsyConfig.FormConfiguration?.Category
                      )}
                      onChange={(e) => {
                        handleChangeClearPIPRequired(
                          e,
                          e.currentTarget.value === "Foster Parent PIP Only"
                        );
                      }}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <div className="mt-lg-4 fst-italic">
                      {getCategoryDefinition(
                        getFieldValue(data.Category),
                        getFieldOptions(
                          formsyConfig.FormConfiguration?.Category
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className="row row-cols-1 row-cols-lg-2">
                  <div className="col">
                    <FormSelect
                      name="Subcategory"
                      label="Subcategory"
                      error={errors.Subcategory}
                      value={getFieldValue(data.Subcategory)}
                      options={getFieldOptions(
                        formsyConfig.FormConfiguration?.Subcategory
                      )}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {getFieldValue(data.Subcategory) === "Other" ? (
                      <FormInput
                        name="SubcategoryOther"
                        error={errors.SubcategoryOther}
                        value={getFieldValue(data.SubcategoryOther)}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                        divClassName="mt-lg-3 pt-lg-3 flex-fill"
                      />
                    ) : null}
                  </div>
                </div>
                {getFieldValue(data.FHState) !== "Ohio" &&
                (getFieldValue(data.FHState) !== "Indiana" ||
                  getFieldValue(data.UploadingCAP) === "No") &&
                getFieldValue(data.Category) !== "Foster Parent PIP Only" ? (
                  <>
                    <FormTextArea
                      name="Description"
                      label="Enter a brief description of what occurred, including any precipitating factors"
                      error={errors.Description}
                      value={getFieldValue(data.Description)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <FormRadioButton
                      name="PIPRequired"
                      label="Does this rule/policy violation require a Performance Improvement Plan (PIP)?"
                      error={errors.PIPRequired}
                      value={data.PIPRequired?.value}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      divClassName="mb-3"
                    />
                  </>
                ) : null}
              </div>
              {getFieldValue(data.FHState) !== "Ohio" &&
              (getFieldValue(data.FHState) !== "Indiana" ||
                getFieldValue(data.UploadingCAP) === "No") &&
              getFieldValue(data.Category) !== "Foster Parent PIP Only" ? (
                <>
                  <div className="h5">
                    Reporting Requirements and Investigations
                  </div>
                  <div className="container-sm">
                    <FormRadioButton
                      name="MustReport"
                      label="Are we required to report this alleged rule/policy violation to the custodial agency?"
                      error={errors.MustReport}
                      value={getFieldValue(data.MustReport)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      divClassName="mb-3"
                    />
                    {getFieldValue(data.MustReport) === "Yes" ? (
                      <>
                        <FormInput
                          name="DateOfReport"
                          label="When was the alleged rule/policy violation reported to the custodial agency (for CTH-1, date entered in DDSN portal)?"
                          error={errors.DateOfReport}
                          value={getFieldValue(data.DateOfReport)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                          type="datetime-local"
                        />
                        <FormInput
                          name="ReportedTo"
                          label="Whom, at the custodial agency (Name, Title), was the alleged rule/policy violation reported to? (CTH-1 may leave this blank)"
                          error={errors.ReportedTo}
                          value={getFieldValue(data.ReportedTo)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </>
                    ) : null}
                    {getFieldValue(data.MustReport) === "No" ? (
                      <FormTextArea
                        name="NoReportReason"
                        label="Why are we not required to report this alleged rule/policy violation to the custodial agency?"
                        error={errors.NoReportReason}
                        value={getFieldValue(data.NoReportReason)}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <div className="row row-cols-1 row-cols-md-2 gy-2 my-3">
                      <div className="col">
                        <FormRadioButton
                          name="Investigation"
                          label="Will there be a third-party or an external investigation?"
                          error={errors.Investigation}
                          value={getFieldValue(data.Investigation)}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                      {getFieldValue(data.Investigation) === "Yes" ? (
                        <div className="col">
                          <FormRadioButton
                            name="ReceivedResults"
                            label="Did SAFY receive the results of the external investigation?"
                            error={errors.ReceivedResults}
                            value={getFieldValue(data.ReceivedResults)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsFactory={commentsFactory}
                            disabled={formDisabled}
                          />
                        </div>
                      ) : null}
                    </div>
                    {getFieldValue(data.Investigation) === "Yes" ? (
                      <FormRadioButton
                        name="HoldInternal"
                        label="Has SAFY been asked to hold off on conducting an internal investigation until the external investigation is complete?"
                        error={errors.HoldInternal}
                        value={getFieldValue(data.HoldInternal)}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                        divClassName="mb-3"
                      />
                    ) : null}
                    <div className="row row-cols-1 row-cols-md-2">
                      <div className="col">
                        <FormSelect
                          name="Impact"
                          label="What impact does this investigation have on the youth currently in the home?"
                          error={errors.Impact}
                          value={getFieldValue(data.Impact)}
                          options={getFieldOptions(
                            formsyConfig.FormConfiguration?.Impact
                          )}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {[
                          "Children placed in respite",
                          "Children transferred to new home",
                        ].includes(getFieldValue(data.Impact)) ? (
                          <FormRadioButton
                            name="Removal"
                            label="Did the State/County dictate the removal of youth from the home?"
                            error={errors.Removal}
                            value={getFieldValue(data.Removal)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsFactory={commentsFactory}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
              {getFieldValue(data.FHState) !== "Ohio" &&
              (getFieldValue(data.FHState) !== "Indiana" ||
                getFieldValue(data.UploadingCAP) === "No") &&
              getFieldValue(data.Investigation) === "Yes" &&
              getFieldValue(data.ReceivedResults) === "Yes" ? (
                <>
                  <div className="h5">External Investigation</div>
                  <div className="container-sm">
                    <div className="row row-cols-1 row-cols-md-2 gy-2">
                      <div className="col">
                        <FormInput
                          name="DateOfExternal"
                          label="When was the external investigation outcome determined?"
                          error={errors.DateOfExternal}
                          value={getFieldValue(data.DateOfExternal)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                          type="date"
                        />
                      </div>
                      <div className="col">
                        <FormSelect
                          name="ExternalOutcome"
                          label="What was the outcome of the external investigation?"
                          error={errors.ExternalOutcome}
                          value={getFieldValue(data.ExternalOutcome)}
                          options={getFieldOptions(
                            formsyConfig.FormConfiguration?.ExternalOutcome
                          )}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <FormTextArea
                      name="ExternalDetails"
                      label="Narrative description of the external investigation and of the outcome"
                      error={errors.ExternalDetails}
                      value={getFieldValue(data.ExternalDetails)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <div className="row row-cols-1 row-cols-md-2 gy-2">
                      <div className="col">
                        <FormRadioButton
                          name="Appeal"
                          label="Will Foster Parent file an appeal?"
                          error={errors.Appeal}
                          value={getFieldValue(data.Appeal)}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                          divClassName="mb-3"
                        />
                      </div>
                      <div className="col">
                        {getFieldValue(data.Appeal) === "Yes" ? (
                          <FormInput
                            name="DateOfAppeal"
                            label="Date of appeal"
                            error={errors.DateOfAppeal}
                            value={getFieldValue(data.DateOfAppeal)}
                            onChange={handleChange}
                            commentsFactory={commentsFactory}
                            disabled={formDisabled}
                            type="date"
                          />
                        ) : null}
                      </div>
                    </div>
                    {getFieldValue(data.Appeal) === "Yes" ? (
                      <FormTextArea
                        name="ResultOfAppeal"
                        label="Result of appeal"
                        error={errors.ResultOfAppeal}
                        value={getFieldValue(data.ResultOfAppeal)}
                        onChange={handleChange}
                        commentsFactory={commentsFactory}
                        disabled={formDisabled}
                        rows="2"
                      />
                    ) : null}
                  </div>
                </>
              ) : null}
              {getFieldValue(data.FHState) !== "Ohio" &&
              (getFieldValue(data.FHState) !== "Indiana" ||
                getFieldValue(data.UploadingCAP) === "No") &&
              getFieldValue(data.Category) !== "Foster Parent PIP Only" &&
              (getFieldValue(data.Investigation) === "No" ||
                getFieldValue(data.HoldInternal) === "No" ||
                (getFieldValue(data.HoldInternal) === "Yes" &&
                  getFieldValue(data.ReceivedResults) === "Yes")) ? (
                <>
                  <div className="h5">Internal Investigation</div>
                  <div className="container-sm">
                    <div className="row row-cols-1 row-cols-md-2 gy-2 mb-3">
                      <div className="col">
                        <FormInput
                          name="DateOfInternal"
                          label="When was the outcome of the internal investigation determined?"
                          error={errors.DateOfInternal}
                          value={getFieldValue(data.DateOfInternal)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                          type="date"
                        />
                      </div>
                      <div className="col">
                        <FormSelect
                          name="InternalOutcome"
                          label="What was the outcome of the internal investigation?"
                          error={errors.InternalOutcome}
                          value={getFieldValue(data.InternalOutcome)}
                          options={getFieldOptions(
                            formsyConfig.FormConfiguration?.InternalOutcome
                          )}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <FormTextArea
                      name="InternalDetails"
                      label="Narrative description of the internal investigation and of the outcome"
                      error={errors.InternalDetails}
                      value={getFieldValue(data.InternalDetails)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <FormSelect
                      name="Status"
                      label="Status of Foster Home"
                      error={errors.Status}
                      value={getFieldValue(data.Status)}
                      options={getFieldOptions(
                        formsyConfig.FormConfiguration?.Status
                      )}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                </>
              ) : null}

              {getFieldValue(data.FHState) !== "Ohio" &&
              (getFieldValue(data.FHState) !== "Indiana" ||
                getFieldValue(data.UploadingCAP) === "No") &&
              (getFieldValue(data.Category) === "Foster Parent PIP Only" ||
                getFieldValue(data.PIPRequired) === "Yes") ? (
                <>
                  <div className="h5">Performance Improvement Plan</div>
                  <div className="container-sm">
                    <FormTextArea
                      name="PIPDescription"
                      label="Description of the Performance Improvement Plan (PIP), including concrete action steps, knowledge/skills to be developed, and expected timelines"
                      error={errors.PIPDescription}
                      value={getFieldValue(data.PIPDescription)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <FormInput
                      name="PIPDateImplemented"
                      label="PIP implementation date"
                      error={errors.PIPDateImplemented}
                      value={getFieldValue(data.PIPDateImplemented)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                    <FormInput
                      name="PIPDateAnticipated"
                      label="Anticipated PIP completion date"
                      error={errors.PIPDateAnticipated}
                      value={getFieldValue(data.PIPDateAnticipated)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                    <div className="row row-cols-1 row-cols-md-2 gy-2">
                      <div className="col">
                        <FormRadioButton
                          name="PIPOnHold"
                          label="Is the home being placed on hold until completion of this PIP?"
                          error={errors.PIPOnHold}
                          value={getFieldValue(data.PIPOnHold)}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {getFieldValue(data.PIPOnHold) === "Yes" ? (
                          <FormInput
                            name="PIPOnHoldDate"
                            label="Date the home was placed on hold"
                            error={errors.PIPOnHoldDate}
                            value={getFieldValue(data.PIPOnHoldDate)}
                            onChange={handleChange}
                            commentsFactory={commentsFactory}
                            disabled={formDisabled}
                            type="date"
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row row-cols-1 row-cols-md-2 gy-2 mt-2">
                      <div className="col">
                        <FormInput
                          name="PIPMonitoredBy"
                          label="Who is accountable for monitoring this PIP?"
                          error={errors.PIPMonitoredBy}
                          value={getFieldValue(data.PIPMonitoredBy)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormInput
                          name="PIPDateCompleted"
                          label="Date Foster Parent completed PIP"
                          error={errors.PIPDateCompleted}
                          value={getFieldValue(data.PIPDateCompleted)}
                          onChange={handleChange}
                          commentsFactory={commentsFactory}
                          disabled={formDisabled}
                          type="date"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              {submissionid ? (
                <>
                  <FileUpload
                    header={
                      <>
                        <div className="h5">Supporting Documents</div>
                      </>
                    }
                    objectId={submissionid}
                    objectType="formsy v2"
                    savedFiles={fileAttachments}
                    printImg={true}
                    loadFiles={loadFiles}
                  />
                  {errors.FileAttachments && (
                    <div className="alert alert-danger">
                      {errors.FileAttachments}
                    </div>
                  )}
                </>
              ) : null}
              {getFilteredSignatures().length > 0 ? (
                <div className="bg-light">
                  <div className="h5">For Supervisor Use Only</div>
                  <div className="container py-2">
                    <div className="fw-bold mb-2 text-danger">
                      {/* Complete the following information to finalize the Rules Investigation form before Final Signing */}
                      This section must be completed in its entirety to Final
                      Sign and lock down this Rules Investigation form
                    </div>
                    <FormInput
                      name="InvestigationClosedDate"
                      label="Date Foster Home Rules Investigation was closed"
                      error={errors.InvestigationClosedDate}
                      value={getFieldValue(data.InvestigationClosedDate)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                      type="date"
                    />
                    <FormCheckbox
                      name="InvestigationComplete"
                      label="All internal and external investigations have been completed and recorded, and any Performance Improvement Plans are completed"
                      error={errors.InvestigationComplete}
                      checked={getFieldValue(data.InvestigationComplete)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <FormCheckbox
                      name="DocumentsUploaded"
                      label="All supporting documents and external investigation reports have been uploaded"
                      error={errors.DocumentsUploaded}
                      checked={getFieldValue(data.DocumentsUploaded)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                    <FormCheckbox
                      name="RulesInvestigationClosed"
                      label="This Foster Home Rules Investigation is now closed"
                      error={errors.RulesInvestigationClosed}
                      checked={getFieldValue(data.RulesInvestigationClosed)}
                      onChange={handleChange}
                      commentsFactory={commentsFactory}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(data);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              signatures={getFilteredSignatures().map((s) => s.ObjectJSON)}
              onSign={saveSignature}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={
                getFilteredSignatures().length === 0 ? "provider" : "full"
              }
              marginBottom="100px"
              collateralOptions={collateralOptions}
            />
            <Comment
              isOpen={showComment}
              comment={commentToEdit}
              onHide={toggleCommentModal}
              onSave={onCommentSave}
              fields={commentFields}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
                shareLogs={getShareLogs()}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default FHRulesInvestigation;
